/**
 * Attendance Tutorial
 *
 * Interactive multi-page tutorial that guides the user through
 * the attendance recording process:
 * Dashboard → CourseDetail → AttendanceView
 *
 * Uses navigateTo for route changes and data-tutorial attributes
 * for element highlighting.
 */

import type { TutorialDefinition, TutorialStep } from './types'
import { useCoursesStore } from '@/stores/courses.store'
import { useSessionsStore } from '@/stores/sessions.store'

function createSteps(): TutorialStep[] {
  const coursesStore = useCoursesStore()
  const sessionsStore = useSessionsStore()

  const firstCourse = coursesStore.courses[0]
  if (!firstCourse) {
    return [
      {
        popover: {
          title: '📋 Anwesenheit erfassen',
          description:
            'Bevor du dieses Tutorial starten kannst, musst du mindestens einen Kurs haben. Gehe zur Startseite und warte, bis deine Kurse geladen sind.',
        },
      },
    ]
  }

  const relevantSession = sessionsStore.getMostRelevantSession(firstCourse.id)
  const sessionId = relevantSession?.id

  // Build steps dynamically based on available data
  const steps: TutorialStep[] = [
    // Step 1: Intro (popover-only on current page)
    {
      popover: {
        title: '📋 Anwesenheit erfassen',
        description:
          'In dieser Anleitung führe ich dich Schritt für Schritt durch die Anwesenheitserfassung — direkt in der App.',
      },
    },

    // Step 2: Navigate to Dashboard, highlight first course
    {
      navigateTo: '/dashboard',
      element: '[data-tutorial="course-list"] > :first-child',
      popover: {
        title: 'Kurs auswählen',
        description:
          'Auf der <strong>Startseite</strong> siehst du deine Kurse. Tippe auf einen Kurs, um seine Termine zu sehen.',
        position: 'bottom',
      },
    },

    // Step 3: Navigate to CourseDetail, highlight first session
    {
      navigateTo: `/course/${firstCourse.id}`,
      element: '.session-card',
      popover: {
        title: 'Termin auswählen',
        description:
          'Hier siehst du alle <strong>Termine</strong> deines Kurses. Tippe auf einen Termin, um die Anwesenheitsliste zu öffnen.',
        position: 'bottom',
      },
    },
  ]

  // Step 4+: Navigate to AttendanceView (only if a session exists)
  if (sessionId) {
    steps.push(
      {
        navigateTo: `/course/${firstCourse.id}/session/${sessionId}/attendance`,
        element: '[data-tutorial="attendance-list"] > .attendance-item:first-child',
        stagePadding: 8,
        popover: {
          title: 'Anwesenheit eintragen',
          description:
            'Tippe auf einen <strong>Teilnehmer</strong>, um ihn als anwesend (grün) oder abwesend (rot) zu markieren.',
          position: 'left',
        },
      },
      {
        element: '[data-tutorial="toggle-all"]',
        popover: {
          title: 'Alle auf einmal',
          description:
            'Mit diesem Button markierst du <strong>alle Teilnehmer</strong> auf einmal als anwesend oder abwesend.',
          position: 'top',
        },
      },
      {
        element: '[data-tutorial="guest-section"]',
        popover: {
          title: 'Gäste hinzufügen',
          description:
            'Über den Bereich <strong>„Gäste"</strong> kannst du Personen hinzufügen, die nicht regulär im Kurs angemeldet sind.',
          position: 'top',
        },
      },
      {
        popover: {
          title: '📶 Auch offline nutzbar',
          description:
            'Die Anwesenheitserfassung funktioniert auch <strong>ohne Internet</strong>. Deine Änderungen werden automatisch synchronisiert, sobald du wieder online bist.',
        },
      },
      {
        popover: {
          title: '✅ Fertig!',
          description:
            "Das war's! Die Anwesenheit wird <strong>automatisch gespeichert</strong> — du musst nichts extra bestätigen.",
        },
      }
    )
  } else {
    // No session available — short ending
    steps.push({
      popover: {
        title: '✅ Fast fertig!',
        description:
          'Sobald ein Termin verfügbar ist, kannst du dort die Anwesenheit deiner Teilnehmer erfassen. Tippe einfach auf den Termin!',
      },
    })
  }

  return steps
}

export const attendanceTutorial: TutorialDefinition = {
  id: 'attendance',
  title: 'Anwesenheit erfassen',
  description: 'Lerne Schritt für Schritt, wie du die Anwesenheit deiner Kursteilnehmer erfasst.',
  icon: 'pi pi-users',
  steps: createSteps,
}
